import React from 'react';
import { useTranslation } from 'react-i18next';
import { EPDModel, EPDStatusDisplayName, EpdGroupDashboardModel } from 'services/EpdClient';
import { A, Aside, H2, Text } from 'styles/Styles.styled';

import InvoiceQuickFacts from './InvoiceQuickFacts';
import { QuickFactBox } from './styles';
import { ProcessStatus } from 'constants/constants';

type TProps = {
  allEpds: EPDModel[];
  allCopyGroups: EpdGroupDashboardModel[];
  companyId: string;
  accountId: string | undefined;
  licenseeSupportEmail: string;
  status: ProcessStatus;
};

const allEpdStatuses = Object.values(EPDStatusDisplayName);

const Sidebox = ({ allEpds, allCopyGroups, accountId, companyId, licenseeSupportEmail, status }: TProps) => {
  const { t } = useTranslation();

  // Delete this when the time comes
  const showCompilerReleaseBanner = new Date() < new Date('2025-02-17T00:00:00+01:00');

  return (
    <Aside>
      {allEpds.length > 0 && (
        <>
          <H2>{t('epdDashboard.quickStats.title')}</H2>
          <Text style={{ marginBottom: '2rem' }}>
            <QuickFactBox>
              {allEpdStatuses
                .map((status) => ({ status, value: allEpds.filter((x) => x.epdState?.statusDisplayName === status).length }))
                .filter((x) => x.value > 0)
                .map((x) => (
                  <React.Fragment key={x.status}>
                    <span>{t(`epdWizard.epdState.status.${x.status}`)}:</span>
                    <strong> {x.value}</strong>
                  </React.Fragment>
                ))}

              <span>{t('epdDashboard.quickStats.copyGroups')}:</span>
              <strong> {allCopyGroups.length}</strong>
            </QuickFactBox>
          </Text>
        </>
      )}

      {showCompilerReleaseBanner && (
        <>
          <H2>New | EPD Compiler</H2>
          <Text style={{ textAlign: 'left', fontWeight: '500' }}>
            <div>The EPD Compiler was launched for the digitalisation of EPDs for construction products.</div>
            <div style={{ marginTop: '1rem' }}>
              Learn more about the{' '}
              <A href="https://environdec.freshdesk.com/en/support/solutions/folders/103000637785" target="_blank">
                EPD Compiler
              </A>
            </div>
          </Text>
        </>
      )}

      <H2>New PCR<br />version alert!</H2>
      <Text style={{ textAlign: 'left', fontWeight: '500' }}>
      The EPD Compiler is now updated to{' '}
        <A href={'https://www.environdec.com/news/pcr-construction-products-v200'} target={'_blank'}>
        PCR 2019:14 v2
        </A>{' '}for Construction Products, as PCR 2019:14 v1.3.4 expires on <b>June 20, 2025</b>. All new and reserved EPDs must follow the updated requirements, including additional inputs in the Compiler format.
        <br /><br />
        Please share your feedback{' '}
        <A href={'https://us18.list-manage.com/survey?u=10fe04046e9685d35b4f4d87c&id=1d12764d21&attribution=false'} target={'_blank'}>
        here
        </A>.
      </Text>

      <H2>New EPD Statuses!</H2>
      <Text style={{ textAlign: 'left', fontWeight: '500' }}>
        We introduce new EPD statuses that help navigate the EPD creation process more easily. Download the{' '}
        <A href={'/EPD_statuses_guide.pdf'} target={'_blank'}>
          new statuses guide.
        </A>
        <br />
      </Text>

      <InvoiceQuickFacts accountId={accountId} companyid={companyId} />
      <H2 style={{ textTransform: 'uppercase' }}> {t('epdDashboard.faq.title')}</H2>
      <Text style={{ textAlign: 'left', fontWeight: '500' }}>
        <strong>Need help with the portal? </strong>
        Check out our{' '}
        <A href={'https://environdec.com/resources/documentation#epdportalinstructions'} target={'_blank'}>
          {'Portal Manual.'}
        </A>
        <br />
        <br />
        {'You can also find answers to the most commonly asked questions about EPDs, PCRs and other subjects in our '}
        <A href={'https://environdec.com/faq'} target={'_blank'}>
          {'FAQ.'}
        </A>
      </Text>
      <br />

      <H2>{'Contact'}</H2>
      <Text style={{ textAlign: 'left', fontWeight: '500' }}>
        For further assistance or feedback, feel free to contact us via e-mail at
        <br />
        {status === ProcessStatus.Fetching ? (
          <>{t('messages.loadingSupportEmail')}</>
        ) : (
          <>
            <A href={`mailto:${licenseeSupportEmail}`}>{licenseeSupportEmail}</A>
          </>
        )}
        <br />
        <br />
        Alternatively, you can use our{' '}
        <A href={'https://environdec.freshdesk.com/en/support/tickets/new'} target={'_blank'}>
          Helpdesk.
        </A>
        <br />
      </Text>
    </Aside>
  );
};

export default Sidebox;
